import { InvoiceHire } from '../../types';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import type { StatCard, FilterConfig } from '../../components/Common';

/** Debit and credit notes live in the invoice-hire collection with a note type */
export const isNoteType = (t?: string) => t === 'debit_note' || t === 'credit_note';

/** Filter dropdown configs for the Client History page */
export function buildClientHistoryFilterConfigs(s: {
  statusFilter: string; setStatusFilter: (v: string) => void;
  clientFilter: string; setClientFilter: (v: string) => void;
  dateFromFilter: string; setDateFromFilter: (v: string) => void;
  dateToFilter: string; setDateToFilter: (v: string) => void;
  amountFromFilter: string; setAmountFromFilter: (v: string) => void;
  amountToFilter: string; setAmountToFilter: (v: string) => void;
  currencyFilter: string; setCurrencyFilter: (v: string) => void;
  clients: any[];
}): FilterConfig[] {
  return [
    { key: 'status', label: 'Status', type: 'select', value: s.statusFilter, onChange: s.setStatusFilter,
      options: [{ value: 'all', label: 'All' }, { value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' }, { value: 'partially_paid', label: 'Partially Paid' }, { value: 'paid', label: 'Paid' }, { value: 'overdue', label: 'Overdue' }] },
    { key: 'client', label: 'Client', type: 'select', value: s.clientFilter, onChange: s.setClientFilter,
      options: [{ value: 'all', label: 'All Clients' }, ...(s.clients || []).map((c: any) => ({ value: c.documentId || String(c.id), label: c.name }))] },
    { key: 'dateFrom', label: 'Date From', type: 'date', value: s.dateFromFilter, onChange: s.setDateFromFilter },
    { key: 'dateTo', label: 'Date To', type: 'date', value: s.dateToFilter, onChange: s.setDateToFilter },
    { key: 'amountFrom', label: 'Amount From', type: 'number', value: s.amountFromFilter, onChange: s.setAmountFromFilter, placeholder: '0' },
    { key: 'amountTo', label: 'Amount To', type: 'number', value: s.amountToFilter, onChange: s.setAmountToFilter, placeholder: '999999' },
    { key: 'currency', label: 'Currency', type: 'select', value: s.currencyFilter, onChange: s.setCurrencyFilter,
      options: [{ value: 'all', label: 'All' }, { value: 'EGP', label: 'EGP' }, { value: 'USD', label: 'USD' }] },
  ];
}

const fmt = (n: any) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * Build the totals cards: one row per currency from summary.byCurrency
 * (count / total / paid / remaining per currency). Falls back to the mixed
 * top-level totals only when the backend summary has no currency breakdown.
 */
export function buildSummaryCardRows(
  summary: any,
  fallbackCount: number,
  currencyFilter: string = 'all'
): StatCard[][] {
  const byCurrency: Record<string, any> = summary?.byCurrency || {};
  // When a currency filter is active, only that currency's row is shown
  // (zeros included, so the cards visibly react to the filter)
  const rows = Object.entries(byCurrency)
    .filter(([code, c]: [string, any]) => currencyFilter !== 'all'
      ? code === currencyFilter
      : (c?.count ?? 0) > 0)
    .map(([code, c]: [string, any]): StatCard[] => ([
      { label: `Invoices (${code})`, value: c.count ?? 0, color: 'blue' },
      { label: `Total (${code})`, value: fmt(c.total), color: 'yellow' },
      { label: `Paid (${code})`, value: fmt(c.paid), color: 'green' },
      { label: `Remaining (${code})`, value: fmt(c.remaining), color: 'red' },
    ]));
  if (rows.length > 0) return rows;
  if (currencyFilter !== 'all' && summary) {
    return [[
      { label: `Invoices (${currencyFilter})`, value: 0, color: 'blue' },
      { label: `Total (${currencyFilter})`, value: 0, color: 'yellow' },
      { label: `Paid (${currencyFilter})`, value: 0, color: 'green' },
      { label: `Remaining (${currencyFilter})`, value: 0, color: 'red' },
    ]];
  }
  return [[
    { label: 'Total Invoices', value: summary?.totalInvoices ?? fallbackCount, color: 'blue' },
    { label: 'Total Amount', value: fmt(summary?.totalAmount), color: 'yellow' },
    { label: 'Total Paid', value: fmt(summary?.totalPaid), color: 'green' },
    { label: 'Total Remaining', value: fmt(summary?.totalRemaining), color: 'red' },
  ]];
}

/**
 * Tab-aware totals cards (one row per currency) computed from the rows the
 * table actually shows, so Invoices and Debit/Credit Notes are never mixed.
 * paid = original − remaining (the backend reduces netTotal as payments land).
 */
export function buildTabCardRows(label: string, hires: InvoiceHire[], legacy: any[] = []): StatCard[][] {
  const by: Record<string, { count: number; total: number; paid: number; remaining: number }> = {};
  const add = (code: string, total: number, paid: number, remaining: number) => {
    if (!by[code]) by[code] = { count: 0, total: 0, paid: 0, remaining: 0 };
    by[code].count += 1; by[code].total += total; by[code].paid += paid; by[code].remaining += remaining;
  };
  hires.forEach((h) => {
    const code = resolveCurrencyCode(h.currency) || 'EGP';
    const total = Number(h.originalTotal ?? h.netTotal) || 0;
    const remaining = Number(h.netTotal) || 0;
    add(code, total, Math.max(0, total - remaining), remaining);
  });
  legacy.forEach((inv: any) => {
    const code = resolveCurrencyCode(inv.currency) || 'EGP';
    add(code, Number(inv.total) || 0, Number(inv.paid) || 0, Number(inv.netRemaining ?? inv.remaining) || 0);
  });
  return Object.entries(by).map(([code, c]): StatCard[] => ([
    { label: `${label} (${code})`, value: c.count, color: 'blue' },
    { label: `Total (${code})`, value: fmt(c.total), color: 'yellow' },
    { label: `Paid (${code})`, value: fmt(c.paid), color: 'green' },
    { label: `Remaining (${code})`, value: fmt(c.remaining), color: 'red' },
  ]));
}

export interface ClientHistoryFilters {
  searchTerm: string;
  statusFilter: string;
  /** All ids (documentId + numeric id) of the selected client, or null for "all" */
  clientIds: Set<string> | null;
  dateFromFilter: string;
  dateToFilter: string;
  amountFromFilter: string;
  amountToFilter: string;
  currencyFilter: string;
}

/** All ids (documentId + numeric id) of the selected client, or null for "all" */
export function buildClientIdSet(clients: any[], clientFilter: string): Set<string> | null {
  if (clientFilter === 'all') return null;
  const c: any = (clients || []).find(
    (cl: any) => cl.documentId === clientFilter || String(cl.id) === String(clientFilter)
  );
  return new Set(
    [clientFilter, c?.documentId, c?.id != null ? String(c.id) : null].filter(Boolean) as string[]
  );
}

/** Apply the Client History page filters to the invoice-hire rows */
export function filterInvoiceHires(
  invoiceHires: InvoiceHire[],
  f: ClientHistoryFilters,
  getClientName: (clientId: string) => string
): InvoiceHire[] {
  return invoiceHires.filter((inv) => {
    const clientName = getClientName(inv.customerId);
    const total = Number(inv.originalTotal ?? inv.netTotal) || 0;
    const matchesSearch = !f.searchTerm || (
      (inv.invoiceNumber || '').toLowerCase().includes(f.searchTerm.toLowerCase()) ||
      clientName.toLowerCase().includes(f.searchTerm.toLowerCase()) ||
      (inv.type || '').toLowerCase().includes(f.searchTerm.toLowerCase()) ||
      (inv.status || '').toLowerCase().includes(f.searchTerm.toLowerCase())
    );
    const matchesStatus = f.statusFilter === 'all' || inv.status === f.statusFilter;
    const matchesClient = !f.clientIds || f.clientIds.has(String(inv.customerId));
    const invoiceDate = new Date(inv.invoiceDate);
    const matchesDateFrom = !f.dateFromFilter || invoiceDate >= new Date(f.dateFromFilter);
    const matchesDateTo = !f.dateToFilter || invoiceDate <= new Date(f.dateToFilter);
    const matchesAmountFrom = !f.amountFromFilter || total >= parseFloat(f.amountFromFilter);
    const matchesAmountTo = !f.amountToFilter || total <= parseFloat(f.amountToFilter);
    const matchesCurrency = f.currencyFilter === 'all' || resolveCurrencyCode(inv.currency) === f.currencyFilter;
    return matchesSearch && matchesStatus && matchesClient &&
      matchesDateFrom && matchesDateTo && matchesAmountFrom && matchesAmountTo && matchesCurrency;
  });
}
